'use strict'

/**
 * DEMO-ONLY IndexedDB blob store for settlement-doc bytes.
 *
 * Answers: "how do I persist bytes locally (for the demo)?"
 *
 * Stand-in for S3 object bytes. Lets uploaded settlement-statement bytes
 * survive page reloads in the frontend-only demo so the "open the doc
 * after refreshing" experience matches what the eventual S3-backed v1
 * will give. No callers outside the sibling `api.js` should touch this
 * store.
 *
 * Hand-rolled rather than via `idb-keyval` so we can keep zero new
 * runtime deps in the demo bundle (idb-keyval is small but it's still
 * surface area).
 *
 * V1 RIP-OUT: delete this file entirely and drop `fake-indexeddb` from
 * devDeps once the real BE lands; the sibling `api.js` is the only
 * consumer.
 */

const DB_NAME = 'ucg-master-lot-settlement-docs'
const DB_VERSION = 1
const STORE_NAME = 'settlementDocBlobs'

// Cached `Promise<IDBDatabase>` so repeat callers share a single open handle
// for the lifetime of the page. `null` means "not yet opened or just reset by
// `__testing__.resetConnection()`".
let connectionPromise = null

const indexedDbHandle = () => {
  // jsdom doesn't ship IndexedDB; the spec wires `fake-indexeddb/auto`,
  // which sets the factory on the jsdom window (also the test global).
  // Production browsers always have it on `window`.
  if (typeof window !== 'undefined' && window.indexedDB) return window.indexedDB
  throw new Error('IndexedDB unavailable; demo blob persistence cannot run')
}

const openConnection = () => {
  if (connectionPromise) return connectionPromise
  connectionPromise = new Promise((resolve, reject) => {
    const req = indexedDbHandle().open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = (event) => {
      const db = event.target.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME)
      }
    }
    req.onsuccess = (event) => resolve(event.target.result)
    req.onerror = (event) => reject(event.target.error || new Error('IndexedDB open failed'))
  })
  return connectionPromise
}

const withStore = async (mode, work) => {
  const db = await openConnection()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, mode)
    const store = tx.objectStore(STORE_NAME)
    const req = work(store)
    req.onsuccess = () => resolve(req.result)
    req.onerror = (event) => reject(event.target.error || new Error('IndexedDB op failed'))
  })
}

export const putSettlementDocBlob = async (id, blob) => {
  if (!id) throw new Error('id required')
  if (!blob) throw new Error('blob required')
  await withStore('readwrite', (store) => store.put(blob, id))
}

export const getSettlementDocBlob = async (id) => {
  if (!id) return null
  const value = await withStore('readonly', (store) => store.get(id))
  return value ?? null
}

export const deleteSettlementDocBlob = async (id) => {
  if (!id) return
  await withStore('readwrite', (store) => store.delete(id))
}

export const clearAllSettlementDocBlobs = async () => {
  await withStore('readwrite', (store) => store.clear())
}

// Test-only escape hatch so specs can swap `window.indexedDB` between
// cases (e.g. quota simulation) without leaking the cached connection.
export const __testing__ = {
  resetConnection() {
    connectionPromise = null
  },
}
